/* ============================================
   IMAGE GALLERY COMPONENT
   The Creative Orchestra - GEMINI Framework
   Image gallery with grid layout and lightbox
   ============================================ */

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image as ImageIcon, 
  ZoomIn, 
  Grid, 
  Rows,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Lightbox from './Lightbox';
import { useTheme } from '@context/ThemeContext';

/**
 * Image Gallery Component
 * Displays images in a responsive grid with lightbox
 * 
 * @param {Object} props
 * @param {Array} props.images - Array of image URLs or objects with {url, caption, alt}
 * @param {string} props.workId - Work ID for image path construction
 * @param {string} props.title - Gallery section title
 */
const ImageGallery = ({ 
  images, 
  workId,
  title = 'Gallery',
  className = '',
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [layout, setLayout] = useState('grid'); // 'grid' | 'masonry'
  const { activeTheme } = useTheme();

  const accentColor = activeTheme?.colors?.accent || '#818CF8';
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  // Normalize images to consistent format
  const normalizedImages = useMemo(() => {
    if (!images || images.length === 0) return [];
    
    return images.map((img, index) => {
      if (typeof img === 'string') {
        // If just a filename, construct full path
        const url = img.startsWith('http') || img.startsWith('/') 
          ? img 
          : `/assets/images/${workId}/${img}`;
        return {
          url,
          alt: `${workId} image ${index + 1}`,
          caption: null,
        };
      }
      return {
        url: img.url || img.src,
        alt: img.alt || `${workId} image ${index + 1}`,
        caption: img.caption,
      };
    });
  }, [images, workId]);

  // Open lightbox at specific index
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (normalizedImages.length === 0) {
    return (
      <section className={`py-12 px-4 ${className}`}>
        <div className="max-w-6xl mx-auto text-center">
          <div className="p-12 rounded-xl bg-white/5 border border-white/10">
            <ImageIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500">Gallery images coming soon...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 px-4 ${className}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-xl font-semibold text-white">{title}</h3>
          
          {/* Layout toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLayout('grid')}
              className={`p-2 rounded-lg transition-colors ${
                layout === 'grid' 
                  ? 'bg-white/10 text-white' 
                  : 'text-gray-500 hover:text-white'
              }`}
              title="Grid layout"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLayout('masonry')}
              className={`p-2 rounded-lg transition-colors ${
                layout === 'masonry' 
                  ? 'bg-white/10 text-white' 
                  : 'text-gray-500 hover:text-white'
              }`}
              title="Masonry layout"
            >
              <Rows className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Gallery grid */}
        <div className={`
          ${layout === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' 
            : 'columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4'
          }
        `}>
          {normalizedImages.map((image, index) => (
            <GalleryItem
              key={index}
              image={image}
              index={index}
              layout={layout}
              accentColor={accentColor}
              accentRgb={accentRgb}
              onClick={() => openLightbox(index)}
            />
          ))}
        </div>

        {/* Image count */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {normalizedImages.length} image{normalizedImages.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={normalizedImages}
        isOpen={lightboxOpen}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onNavigate={setLightboxIndex}
      />
    </section>
  );
};

/**
 * Gallery Item Component
 */
const GalleryItem = ({ 
  image, 
  index, 
  layout,
  accentColor,
  accentRgb,
  onClick,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  return (
    <motion.div
      className={`
        relative overflow-hidden rounded-xl cursor-pointer group
        ${layout === 'grid' ? 'aspect-video' : 'break-inside-avoid'}
      `}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      onClick={onClick}
    >
      {/* Loading placeholder */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-white/5 animate-pulse flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-gray-600" />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
          <div className="text-center">
            <ImageIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Failed to load</p>
          </div>
        </div>
      )}

      {/* Image */}
      <img
        src={image.url}
        alt={image.alt}
        className={`
          w-full h-full object-cover transition-all duration-500
          group-hover:scale-105
          ${isLoaded ? 'opacity-100' : 'opacity-0'}
        `}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
      />

      {/* Hover overlay */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100
          transition-opacity duration-300 flex items-center justify-center"
        style={{
          background: `linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(${accentRgb}, 0.2) 100%)`,
        }}
      >
        <motion.div
          className="p-3 rounded-full"
          style={{ background: `rgba(${accentRgb}, 0.3)` }}
          initial={{ scale: 0.8 }}
          whileHover={{ scale: 1 }}
        >
          <ZoomIn className="w-6 h-6 text-white" />
        </motion.div>
      </div>

      {/* Caption */}
      {image.caption && (
        <div className="absolute bottom-0 left-0 right-0 p-3 
          bg-gradient-to-t from-black/80 to-transparent
          translate-y-full group-hover:translate-y-0 transition-transform">
          <p className="text-sm text-white">{image.caption}</p>
        </div>
      )}

      {/* Border */}
      <div 
        className="absolute inset-0 rounded-xl border pointer-events-none
          opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ borderColor: `rgba(${accentRgb}, 0.5)` }}
      />
    </motion.div>
  );
};

/**
 * Featured Image Component (single large image)
 */
export const FeaturedImage = ({ 
  image, 
  workId,
  className = '',
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { activeTheme } = useTheme();
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  const normalizedImage = typeof image === 'string'
    ? {
        url: image.startsWith('http') || image.startsWith('/') 
          ? image 
          : `/assets/images/${workId}/${image}`,
        alt: `${workId} featured image`,
      }
    : image;

  return (
    <>
      <motion.div
        className={`relative overflow-hidden rounded-2xl cursor-pointer group ${className}`}
        whileHover={{ y: -4 }}
        onClick={() => setLightboxOpen(true)}
      >
        <img
          src={normalizedImage.url}
          alt={normalizedImage.alt}
          className="w-full h-auto object-cover transition-transform duration-500
            group-hover:scale-105"
        />

        {/* Hover overlay */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100
            transition-opacity duration-300 flex items-center justify-center"
          style={{
            background: `linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(${accentRgb}, 0.1) 100%)`,
          }}
        >
          <div 
            className="p-4 rounded-full"
            style={{ background: `rgba(${accentRgb}, 0.3)` }}
          >
            <ZoomIn className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Caption */}
        {normalizedImage.caption && (
          <div className="absolute bottom-0 left-0 right-0 p-4 
            bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white">{normalizedImage.caption}</p>
          </div>
        )}
      </motion.div>

      <Lightbox
        images={[normalizedImage]}
        isOpen={lightboxOpen}
        currentIndex={0}
        onClose={() => setLightboxOpen(false)}
        onNavigate={() => {}}
      />
    </>
  );
};

/**
 * Thumbnail Strip Component
 */
export const ThumbnailStrip = ({
  images,
  workId,
  currentIndex = 0,
  onSelect,
  className = '',
}) => {
  const normalizedImages = useMemo(() => {
    if (!images) return [];
    return images.map((img, index) => ({
      url: typeof img === 'string' 
        ? (img.startsWith('http') || img.startsWith('/') ? img : `/assets/images/${workId}/${img}`)
        : img.url,
      alt: typeof img === 'string' ? `Image ${index + 1}` : img.alt,
    }));
  }, [images, workId]);

  return (
    <div className={`flex gap-2 overflow-x-auto pb-2 ${className}`}>
      {normalizedImages.map((image, index) => (
        <button
          key={index}
          onClick={() => onSelect?.(index)}
          className={`
            flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden
            border-2 transition-all
            ${currentIndex === index 
              ? 'border-indigo-500 ring-2 ring-indigo-500/30' 
              : 'border-transparent hover:border-white/30'
            }
          `}
        >
          <img
            src={image.url}
            alt={image.alt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
};

export default ImageGallery;
